import { distinctUntilChanged, filter, map, Observable, of, shareReplay, startWith, switchMap } from 'rxjs';
import { ChangesDetection } from '../../../../change-detection';
import { I_ObservationResponse } from '../api/observation.response';
import { C_Observation } from './observation.model';
import { E_ChangeAction } from '../../../../../enums/change-action.enum';

export class C_ObservationWrapperList extends ChangesDetection<T_ObservationWrapperListChange> {
  private list: Map<string, Map<string, C_ObservationWrapper>> = new Map(); // Nivel 1: PatientId -> Nivel 2: Map<ObsName, C_ObservationWrapper>

  private cachedSubs = new Map<string, Observable<C_ObservationWrapper | undefined>>(); // Cache de observables indexado por una clave compuesta "patientId_obsName"

  constructor() {
    super();
  }

  /**
   * Procesa la entrada de una observación asociada a un paciente
   */
  setObservation(obs: I_ObservationResponse & { localId: symbol }) {
    const obsKey = obs.name;
    const patientId = obs.patientId;
    if (!obsKey || !patientId) return;

    let patientMap = this.list.get(patientId);
    if (!patientMap) {
      patientMap = new Map<string, C_ObservationWrapper>();
      this.list.set(patientId, patientMap);
    }

    if (patientMap.has(obsKey)) {
      // UPDATE: Reciclamos instancia
      const currentObs: C_ObservationWrapper = patientMap.get(obsKey)!;
      currentObs.update(obs);

      this.forceUpdateDetection({
        action: E_ChangeAction.Update,
        patientId,
        name: obsKey,
        data: obs
      });
    } else {
      // ADD: Nueva instancia
      const newObs = new C_ObservationWrapper(obs);
      patientMap.set(obsKey, newObs);

      this.forceUpdateDetection({
        action: E_ChangeAction.Add,
        patientId,
        name: obsKey,
        data: newObs,
      });
    }
  }

  /**
     * Procesa la entrada masiva trackeando granularmente los elementos afectados
     */
  setObservationsBulk(obsList: (I_ObservationResponse & { localId: symbol })[]) {
    const affected = new Set<string>();

    obsList.forEach(obs => {
      const obsKey = obs.name;
      const patientId = obs.patientId;
      if (!obsKey || !patientId) return;

      let patientMap = this.list.get(patientId);
      if (!patientMap) {
        patientMap = new Map<string, C_ObservationWrapper>();
        this.list.set(patientId, patientMap);
      }

      // Registrar clave compuesta idéntica a la del caché de suscripciones
      affected.add(`${patientId}_${obsKey}`);

      if (patientMap.has(obsKey)) {
        const currentObs: C_ObservationWrapper = patientMap.get(obsKey)!;
        currentObs.update(obs);
      } else {
        const newObs = new C_ObservationWrapper(obs);
        patientMap.set(obsKey, newObs);
      }
    });

    this.forceUpdateDetection({ action: E_ChangeAction.AddBulk, affected });
  }

  getObs(patId: string, obsKey: string): C_ObservationWrapper | undefined {
    return this.list.get(patId)?.get(obsKey);
  }

  getAllObsFromPatient(patId: string): C_ObservationWrapper[] {
    return Array.from(this.list.get(patId)?.values() ?? []);
  }

  /**
   * Obtiene el flujo reactivo optimizado de una observación para un paciente concreto
   */
  getLiveObs(patientId: string, name: string): Observable<C_ObservationWrapper | undefined> {
    const cacheKey = `${patientId}_${name}`;
    if (this.cachedSubs.has(cacheKey)) return this.cachedSubs.get(cacheKey)!;

    const stream$ = this.getForcedChanges().pipe(
      startWith(null),
      filter((change) => {
        if (!change) return true; // Permite el disparo inicial (startWith)
        if (change.action === E_ChangeAction.Reset) return true;
        if (change.action === E_ChangeAction.AddBulk) return change.affected.has(cacheKey); // Filtrado granular en Bulk
        // Filtrado para acciones Add / Update / Delete atómicas
        return change.patientId === patientId && change.name === name;
      }),
      map(() => {
        const pMap = this.list.get(patientId);
        return pMap ? pMap.get(name) : undefined;
      }),
      distinctUntilChanged(),
      switchMap(obs => {
        if (!obs) return of(undefined);

        // Escuchamos los cambios internos de la propia instancia (update())
        return obs.getForcedChanges().pipe(
          startWith(obs),
          map(() => obs)
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cachedSubs.set(cacheKey, stream$);
    return stream$;
  }

  /**
   * Helper para obtener de forma síncrona todas las obs de un paciente
   */
  getPatientObservations(patientId: string): C_ObservationWrapper[] {
    const pMap = this.list.get(patientId);
    return pMap ? Array.from(pMap.values()) : [];
  }

  clearSubs() {
    this.cachedSubs.clear();
    this.forceUpdateDetection({ action: E_ChangeAction.Reset });
  }
}
export type T_ObservationWrapperListChange =
  | { action: E_ChangeAction.Add; patientId: string; name: string; data: C_ObservationWrapper }
  | { action: E_ChangeAction.Update; patientId: string; name: string; data: I_ObservationResponse }
  | { action: E_ChangeAction.Delete; patientId: string; name: string }
  | { action: E_ChangeAction.AddBulk; affected: Set<string> }
  | { action: E_ChangeAction.Reset };

export class C_ObservationWrapper extends ChangesDetection {
  /**
   * Id local para evitar reprocesado de mensajes debido a los detectores de cambios
   */
  localId: symbol = Symbol();
  id: string = '';
  name?: string;
  currentValue?: C_Observation;
  previousValue?: C_Observation;
  constructor(json?: I_ObservationResponse & { localId: symbol }) {
    super();
    if (!json) return;
    this.initializeFromJson(json);
  }

  initializeFromJson(json: I_ObservationResponse & { localId: symbol }): void {
    if (json.localId == this.localId) return;
    this.localId = json.localId;
    this.id = json.id + '';
    this.name = json.name;
    this.currentValue = new C_Observation(json);
    this.forceUpdateDetection();
  }

  update(json?: I_ObservationResponse & { localId: symbol }): void {
    if (!json) return;
    if (json.localId === this.localId) return;

    this.localId = json.localId;
    this.id = json.id + '';
    this.name = json.name;

    const incomingTime = json.time ? new Date(json.time).valueOf() : 0;
    const currentTime = this.currentValue?.time ? this.currentValue.time.valueOf() : 0;

    if (!this.currentValue || incomingTime > currentTime) {
      this.previousValue = this.currentValue?.clone();
      if (this.currentValue) {
        this.currentValue.initializeFromJson(json);
      } else {
        this.currentValue = new C_Observation(json);
      }
    } 
    else if (incomingTime === currentTime) {
      this.currentValue.initializeFromJson(json);
    }
    else {
      const previousTime = this.previousValue?.time ? this.previousValue.time.valueOf() : 0;
      if (incomingTime > previousTime) {
        this.previousValue = new C_Observation(json);
      }
    }
    this.forceUpdateDetection();
  }

  /**
   * Función que devuelve si  el valor actual de observacion ha expirado
   */
  public isExpired(): boolean {
    if (this.currentValue != null) {
      return this.currentValue.isExpired();
    }
    return false;
  }
}
